import { Share } from '@/components/transparency/Share';
import { API_PREFIX_LEGACY } from '@/const/api';
import type { Data, Region } from '@/pageTemplate/LemonadeGoesGlobalPageTemplate';
import errorIconImage from '@/static/assets/attention_mark_white.png';
import loveImage from '@/static/assets/lemonade_goes_global/love.png';
import { Flex, useDialogContext } from '@lemonade-hq/cdk';
import type { Locale } from '@lemonade-hq/lemonation';
import {
  BaseDialog,
  Button,
  DialogBody,
  DialogHeader,
  InputText,
  Option,
  OptionsList,
  Select,
} from '@lemonade-hq/nuis';
import { MEDIA_QUERIES } from '@lemonade-hq/nuis-atomic';
import { Client } from '@lemonade-hq/nuiza';
import { isValidEmail } from '@lemonade-hq/ts-helpers';
import Image from 'next/image';
import type React from 'react';
import { useCallback, useMemo, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { MainHeader, SectionParagraph } from '../shared/Content';
import {
  FORM_SUBMIT_MESSAGE,
  PLACEHOLDERS,
  REGION,
  TRANSLATED_EU_COUNTRIES,
  TRANSLATED_PRODUCTS,
  TRANSLATED_SUBMIT_BUTTON,
  US_STATES,
} from './formData';

const pulseAnimation = keyframes`
    0% { transform: scale(0.8); }
    100% { transform: scale(1.1); }
`;

const StyledInput = styled(InputText)`
  width: 100%;
  height: 100%;
  input {
    height: 100%;
  }
`;

const LoveImage = styled(Image)`
  will-change: transform;
  margin-bottom: 32px;
  animation: ${pulseAnimation} 0.8s ease-in-out alternate-reverse infinite;
`;

const StyledOption = styled(Option)`
  text-transform: capitalize;
`;

const StyledSelect = styled(Select)`
  text-transform: capitalize;
`;

const StyledForm = styled.form`
  display: flex;
  position: relative;
  padding-top: 40px;
  gap: 16px;
  margin-top: 15px;
  width: 100%;
  max-width: 1000px;
  flex-direction: column;

  ${MEDIA_QUERIES.tabletAndDesktop`
    margin-top: 25px;
    flex-direction: row;
  `}
`;

const ErrorWrapper = styled(Flex)`
  position: absolute;
  top: 0;
  left: 0;
  gap: 6px;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.pink};

  ${SectionParagraph} {
    font-size: 14px;
  }
`;

const StyledFormSectionWrapper = styled(Flex)`
  flex: 1 1 0%;
  align-items: flex-stretch;
`;

interface SuccessDialogProps {
  locale: Locale;
  location: string;
}

const SuccessDialog: React.FC<SuccessDialogProps> = ({ locale, location }) => {
  return (
    <BaseDialog>
      <DialogHeader>
        <LoveImage src={loveImage} alt="love" />
      </DialogHeader>
      <DialogBody>
        <Flex flexDirection="column" px="30px" pb="10px" justifyContent="center">
          <MainHeader>{FORM_SUBMIT_MESSAGE[locale].title}</MainHeader>

          <SectionParagraph>
            {FORM_SUBMIT_MESSAGE[locale].message}
            <span style={{ textTransform: 'capitalize' }}>{location}</span>
          </SectionParagraph>

          <Flex mt="30px" justifyContent="center">
            <SectionParagraph>{FORM_SUBMIT_MESSAGE[locale].tell_your_friends}</SectionParagraph>
          </Flex>
          <Flex width="100%">
            <Share mt={20} />
          </Flex>
        </Flex>
      </DialogBody>
    </BaseDialog>
  );
};

interface FormProps {
  locale: Locale;
  data: Data;
}

export const Form: React.FC<FormProps> = ({ locale, data }) => {
  const { openDialog } = useDialogContext();
  const [state, setState] = useState<{
    region: Region;
    product: string | undefined;
    location: string | undefined;
    email: string;
    status: 'idle' | 'loading' | 'success' | 'error';
  }>({
    region: locale === 'en-US' ? 'us' : 'eu',
    product: '',
    location: '', // EU countries or US states
    email: '',
    status: 'idle',
  });

  const translatedLocation = useMemo(() => {
    return state.region === 'eu'
      ? TRANSLATED_EU_COUNTRIES[locale][state.location as keyof (typeof TRANSLATED_EU_COUNTRIES)[typeof locale]]
      : US_STATES[state.location as keyof typeof US_STATES];
  }, [state.location, locale, state.region]);

  const onOpenDialog = useCallback(() => {
    openDialog(<SuccessDialog locale={locale} location={translatedLocation ?? ''} />);
  }, [openDialog, locale, translatedLocation]);

  const availablePlacesPerProduct = useMemo(() => {
    const availableFromData = data[state.region ?? 'us'].find(i => i.product === state.product)?.available ?? [];

    // return states
    if (state.region === 'us') {
      return Object.keys(US_STATES)
        .map(key => (!availableFromData.includes(key.toLowerCase()) ? key : undefined))
        .filter(Boolean);
    }

    // return countries
    return Object.keys(TRANSLATED_EU_COUNTRIES[locale])
      .map(key => {
        const country = key.toLowerCase();
        return !availableFromData.includes(country) ? country : undefined;
      })
      .filter(Boolean);
  }, [state.product, state.region, data, locale]);

  const renderLocation = useCallback(
    (item: string) => {
      return state.region === 'eu'
        ? TRANSLATED_EU_COUNTRIES[locale][item as keyof (typeof TRANSLATED_EU_COUNTRIES)[typeof locale]]
        : (US_STATES[item as keyof typeof US_STATES] ?? item);
    },
    [state.region, locale],
  );

  const isFormFieldDisabled = useMemo(() => {
    return state.status === 'loading' || state.status === 'success';
  }, [state]);

  const updateState = useCallback((subState: Partial<typeof state>): void => {
    setState(formState => {
      return {
        ...formState,
        ...subState,
      };
    });
  }, []);

  const isFormDisabled = useMemo(() => {
    return isFormFieldDisabled || !isValidEmail(state.email) || !state.location || !state.product;
  }, [state, isFormFieldDisabled]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      if (isFormDisabled) return;

      updateState({ status: 'loading' });

      try {
        await Client.post(`${API_PREFIX_LEGACY}/subscribe`, {
          email: state.email,
          [state.region === 'us' ? 'state' : 'country']: state.location,
          product: state.product,
          origin: '47states_page',
        });
        updateState({ status: 'success' });
        onOpenDialog();
      } catch (e: unknown) {
        console.log(e);
        updateState({ status: 'error' });
      }
    },
    [state, onOpenDialog, updateState, isFormDisabled],
  );

  return (
    <>
      <StyledForm onSubmit={handleSubmit}>
        {state.status === 'error' && (
          <ErrorWrapper>
            <Image src={errorIconImage} alt="" height={16} width={16} />
            <SectionParagraph>{FORM_SUBMIT_MESSAGE[locale].error}</SectionParagraph>
          </ErrorWrapper>
        )}

        {/* region */}
        <StyledFormSectionWrapper>
          <StyledSelect
            floatingLabel={true}
            placeholder={PLACEHOLDERS[locale].REGION}
            value={state.region}
            disabled={isFormFieldDisabled}
            onChange={region =>
              updateState({
                region: region as Region,
                location: undefined,
                product: undefined,
              })
            }
          >
            <OptionsList>
              <StyledOption value="us">{REGION[locale].us}</StyledOption>
              <StyledOption value="eu">{REGION[locale].eu}</StyledOption>
            </OptionsList>
          </StyledSelect>
        </StyledFormSectionWrapper>

        {/* product */}
        <StyledFormSectionWrapper>
          <StyledSelect
            floatingLabel={true}
            disabled={isFormFieldDisabled}
            value={state.product}
            onChange={product => updateState({ product })}
            placeholder={PLACEHOLDERS[locale].PRODUCT}
          >
            <OptionsList>
              {data[state.region ?? 'us'].map(item => (
                <StyledOption key={item.product} value={item.product}>
                  {TRANSLATED_PRODUCTS[locale][item.product]}
                </StyledOption>
              ))}
            </OptionsList>
          </StyledSelect>
        </StyledFormSectionWrapper>

        {/* places */}
        <StyledFormSectionWrapper>
          <StyledSelect
            floatingLabel={true}
            disabled={isFormFieldDisabled}
            value={state.location}
            onChange={location => updateState({ location })}
            placeholder={PLACEHOLDERS[locale].LOCATION[state.region]}
          >
            <OptionsList>
              {availablePlacesPerProduct.map(item => (
                <StyledOption key={item} value={item}>
                  {renderLocation(item ?? '')}
                </StyledOption>
              ))}
            </OptionsList>
          </StyledSelect>
        </StyledFormSectionWrapper>

        <StyledFormSectionWrapper>
          <StyledInput
            floatingLabel={true}
            disabled={isFormFieldDisabled}
            placeholder={PLACEHOLDERS[locale].EMAIL}
            value={state.email}
            onChange={e => updateState({ email: e.target.value })}
            type="email"
          />
        </StyledFormSectionWrapper>

        <StyledFormSectionWrapper>
          <Button
            disabled={isFormDisabled}
            type="submit"
            flavor="primary"
            glowing
            style={{ paddingInline: '25px', width: '100%' }}
            centerLoader
            isLoading={state.status === 'loading'}
          >
            {TRANSLATED_SUBMIT_BUTTON[locale]}
          </Button>
        </StyledFormSectionWrapper>
      </StyledForm>
    </>
  );
};
